import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bot, ShieldCheck } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { appTexts } from "@/i18n/appTexts";
import { FaceScan } from "@/components/register/FaceScan";
import { PasskeySetup } from "@/components/register/PasskeySetup";
import { BackArrow } from "@/components/BackArrow";

const inputCls = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#B026FF]/60 placeholder:text-white/30";

// Temporary relaxed signup (phone + password only). Flip to false to restore the full 4-step biometric wizard.
const QUICK_SIGNUP = true;

const QuickRegister = ({ t, lang, setLang }) => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const valid = /^\d{10}$/.test(phone) && password.length >= 6;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError("");
    try {
      await register({ phone, password, passkey_verified: false, face_verified: false });
      navigate("/birth");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white flex items-center justify-center px-6 relative" data-testid="quick-register-page">
      <BackArrow />
      <div className="absolute top-6 right-6 flex gap-1 bg-white/5 border border-white/10 rounded-full p-1">
        {["en", "es"].map((l) => (
          <button key={l} onClick={() => setLang(l)} className={`text-[10px] font-bold px-3 py-1 rounded-full uppercase ${lang === l ? "bg-[#B026FF] text-white" : "text-white/50"}`}>{l}</button>
        ))}
      </div>
      <div className="w-full max-w-md bg-[#0F0F1A]/80 border border-white/10 rounded-3xl p-8">
        <div className="flex items-center justify-center gap-2 font-heading font-black mb-2">
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#B026FF] to-[#00F5FF] flex items-center justify-center"><Bot size={20} /></span>
          AVATAR <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#B026FF] to-[#00F5FF]">4 U</span>
        </div>
        <h1 className="font-heading font-black text-xl text-center mb-1">{t.quickTitle}</h1>
        <p className="text-xs text-[#A0A0B0] text-center mb-7">{t.quickSub}</p>
        <form onSubmit={submit} className="space-y-4">
          <input
            data-testid="quick-phone-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
            placeholder={t.quickPhone}
            inputMode="numeric"
            className={inputCls}
          />
          <input
            data-testid="quick-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t.password}
            className={inputCls}
          />
          {error && <p className="text-xs text-red-400" data-testid="quick-register-error">{error}</p>}
          <button
            data-testid="quick-register-btn"
            type="submit"
            disabled={!valid || busy}
            className="w-full py-4 rounded-full font-heading font-black text-sm uppercase tracking-widest bg-gradient-to-r from-[#B026FF] to-[#00F5FF] shadow-[0_0_25px_rgba(176,38,255,0.5)] hover:shadow-[0_0_40px_rgba(176,38,255,0.75)] transition-all disabled:opacity-40"
          >
            {busy ? "..." : t.quickBtn}
          </button>
        </form>
        <p className="flex items-start gap-2 text-[10px] text-white/40 mt-5 leading-relaxed">
          <ShieldCheck size={13} className="text-[#00FFAA] shrink-0 mt-0.5" /> {t.quickNote}
        </p>
        <p className="text-xs text-white/40 text-center mt-5">
          {t.haveAccount} <Link to="/login" className="text-[#00F5FF] font-bold">Login</Link>
        </p>
      </div>
    </div>
  );
};

export default function Register() {
  const { register } = useAuth();
  const { lang, setLang } = useLang();
  const t = appTexts[lang].auth;
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ email: "", phone: "", password: "", name: "", username: "", avatar_name: "", avatar_personality: "friendly" });
  const [contactMethod, setContactMethod] = useState("email");
  const [faceOk, setFaceOk] = useState(false);
  const [passkeyOk, setPasskeyOk] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (QUICK_SIGNUP) return <QuickRegister t={t} lang={lang} setLang={setLang} />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const canNext = () => {
    if (step === 0) {
      const contactOk = contactMethod === "email" ? form.email.includes("@") : /^\d{10}$/.test(form.phone);
      return contactOk && form.password.length >= 6;
    }
    if (step === 1) return form.name.length > 0 && form.username.length >= 3;
    if (step === 2) return form.avatar_name.length > 0;
    return faceOk && passkeyOk;
  };

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await register({
        ...form,
        email: contactMethod === "email" ? form.email : undefined,
        phone: contactMethod === "phone" ? form.phone : undefined,
        face_verified: faceOk, passkey_verified: passkeyOk,
      });
      navigate("/birth");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const titles = [t.step1Title, t.step2Title, t.step3Title, t.step4Title];

  return (
    <main className="min-h-screen bg-[#05050A] text-white flex items-center justify-center px-6 py-12 relative overflow-hidden">
      <div className="absolute top-[-10%] right-[-15%] w-[450px] h-[450px] bg-[#B026FF] blur-[150px] opacity-25 rounded-full" />
      <div className="absolute bottom-[-15%] left-[-10%] w-[400px] h-[400px] bg-[#00F5FF] blur-[150px] opacity-15 rounded-full" />
      <div className="absolute top-6 left-6 z-20"><BackArrow /></div>
      <div className="absolute top-6 right-6 flex gap-1 bg-white/5 border border-white/10 rounded-full p-1 z-20">
        {["en", "es"].map((l) => (
          <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}-btn`}
            className={`text-xs font-bold px-3 py-1 rounded-full uppercase ${lang === l ? "bg-[#B026FF] text-white" : "text-white/50"}`}>{l}</button>
        ))}
      </div>

      <div className="relative w-full max-w-2xl bg-[#0F0F1A]/80 backdrop-blur-2xl border border-white/10 rounded-3xl p-8 z-10" data-testid="register-card">
        <Link to="/" className="flex items-center gap-2 justify-center mb-4 font-heading font-black text-lg">
          <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#B026FF] to-[#00F5FF] flex items-center justify-center"><Bot size={18} /></span>
          AVATAR <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#B026FF] to-[#00F5FF]">4 U</span>
        </Link>

        <div className="flex items-center justify-center gap-2 mb-8">
          {t.steps.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                i === step ? "bg-[#B026FF]/20 border-[#B026FF]/60 text-white" : i < step ? "bg-[#00FFAA]/10 border-[#00FFAA]/40 text-[#00FFAA]" : "border-white/10 text-white/30"
              }`} data-testid={`step-indicator-${i}`}>
                <span>{i + 1}</span>
                <span className="hidden sm:inline">{s}</span>
              </div>
              {i < 3 && <div className="w-4 h-px bg-white/15" />}
            </div>
          ))}
        </div>

        <h1 className="font-heading text-xl font-black text-center mb-6 flex items-center justify-center gap-2" data-testid="register-step-title">
          {step === 3 && <ShieldCheck size={20} className="text-[#00FFAA]" />}
          {titles[step]}
        </h1>

        {step === 0 && (
          <div className="space-y-4 max-w-md mx-auto">
            <div className="flex gap-2" data-testid="contact-method-toggle">
              {["email", "phone"].map((m) => (
                <button key={m} type="button" data-testid={`contact-${m}-btn`} onClick={() => setContactMethod(m)}
                  className={`flex-1 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest border transition-colors ${
                    contactMethod === m ? "border-[#00F5FF]/60 bg-[#00F5FF]/10 text-[#00F5FF]" : "border-white/10 bg-white/5 text-white/50"
                  }`}>
                  {m === "email" ? t.email : lang === "es" ? "Celular (10 dígitos)" : "Cell phone (10 digits)"}
                </button>
              ))}
            </div>
            {contactMethod === "email" ? (
              <input data-testid="reg-email-input" type="email" value={form.email} onChange={set("email")} placeholder={t.email} className={inputCls} />
            ) : (
              <input data-testid="reg-phone-input" type="tel" maxLength={10} value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "") })}
                placeholder={lang === "es" ? "5512345678 (10 dígitos)" : "5512345678 (10 digits)"} className={inputCls} />
            )}
            <input data-testid="reg-password-input" type="password" value={form.password} onChange={set("password")} placeholder={`${t.password} (6+)`} className={inputCls} />
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4 max-w-md mx-auto">
            <input data-testid="reg-name-input" value={form.name} onChange={set("name")} placeholder={t.name} className={inputCls} />
            <input data-testid="reg-username-input" value={form.username} onChange={set("username")} placeholder={t.username} className={inputCls} />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5 max-w-md mx-auto">
            <input data-testid="reg-avatar-name-input" value={form.avatar_name} onChange={set("avatar_name")} placeholder={t.avatarName} className={inputCls} />
            <div>
              <p className="text-xs uppercase tracking-widest text-white/50 font-bold mb-3">{t.personality}</p>
              <div className="grid grid-cols-2 gap-3">
                {t.personalities.map((p) => (
                  <button key={p.id} type="button" data-testid={`personality-${p.id}`}
                    onClick={() => setForm({ ...form, avatar_personality: p.id })}
                    className={`p-3 rounded-xl border text-xs font-bold transition-colors ${
                      form.avatar_personality === p.id ? "border-[#00F5FF]/60 bg-[#00F5FF]/10 text-[#00F5FF]" : "border-white/10 bg-white/5 text-white/60 hover:bg-white/10"
                    }`}>{p.label}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <FaceScan t={t} verified={faceOk} onVerified={() => setFaceOk(true)} />
            <div className="mt-5">
              <PasskeySetup t={t} verified={passkeyOk} onVerified={() => setPasskeyOk(true)} identifier={contactMethod === "email" ? form.email : form.phone} />
            </div>
            {!(faceOk && passkeyOk) && <p className="text-[10px] text-white/40 text-center mt-4 uppercase tracking-widest">{t.bothRequired}</p>}
          </div>
        )}

        {error && <p data-testid="register-error" className="text-red-400 text-xs text-center mt-4">{error}</p>}

        <div className="flex items-center justify-between mt-8 max-w-md mx-auto md:max-w-full">
          <button type="button" data-testid="reg-back-btn" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
            className="px-6 py-3 rounded-full text-xs font-bold uppercase tracking-widest bg-white/5 border border-white/15 disabled:opacity-30">
            {t.back}
          </button>
          {step < 3 ? (
            <button type="button" data-testid="reg-next-btn" onClick={() => setStep(step + 1)} disabled={!canNext()}
              className="px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest bg-[#B026FF] shadow-[0_0_20px_rgba(176,38,255,0.45)] disabled:opacity-40 disabled:shadow-none">
              {t.next}
            </button>
          ) : (
            <button type="button" data-testid="complete-registration-btn" onClick={submit} disabled={!canNext() || busy}
              className="px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest bg-gradient-to-r from-[#00FFAA] to-[#00F5FF] text-[#05050A] shadow-[0_0_25px_rgba(0,255,170,0.4)] disabled:opacity-40 disabled:shadow-none">
              {busy ? "..." : t.completeReg}
            </button>
          )}
        </div>

        <p className="text-xs text-[#A0A0B0] text-center mt-6">
          {t.haveAccount}{" "}
          <Link to="/login" data-testid="goto-login-link" className="text-[#00F5FF] font-bold hover:underline">{t.loginLink}</Link>
        </p>
      </div>
    </main>
  );
}
